// Currency symbols map
export const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  CHF: "CHF",
  CAD: "C$",
  AUD: "A$",
  CNY: "¥",
  HKD: "HK$",
  SGD: "S$",
  INR: "₹",
  KRW: "₩",
  BRL: "R$",
  MXN: "MX$",
  SEK: "kr",
  NOK: "kr",
  DKK: "kr",
  PLN: "zł",
  CZK: "Kč",
  TRY: "₺",
  RUB: "₽",
  ZAR: "R",
  ILS: "₪",
  THB: "฿",
  IDR: "Rp",
  MYR: "RM",
  PHP: "₱",
  NZD: "NZ$",
  TWD: "NT$",
  BTC: "₿",
  ETH: "Ξ",
  USDT: "$",
  USDC: "$",
};

// Currency full names
export const CURRENCY_NAMES: Record<string, string> = {
  USD: "US Dollar",
  EUR: "Euro",
  GBP: "British Pound",
  JPY: "Japanese Yen",
  CHF: "Swiss Franc",
  CAD: "Canadian Dollar",
  AUD: "Australian Dollar",
  CNY: "Chinese Yuan",
  HKD: "Hong Kong Dollar",
  SGD: "Singapore Dollar",
  INR: "Indian Rupee",
  KRW: "South Korean Won",
  BRL: "Brazilian Real",
  MXN: "Mexican Peso",
  SEK: "Swedish Krona",
  NOK: "Norwegian Krone",
  DKK: "Danish Krone",
  PLN: "Polish Zloty",
  CZK: "Czech Koruna",
  TRY: "Turkish Lira",
  RUB: "Russian Ruble",
  ZAR: "South African Rand",
  ILS: "Israeli Shekel",
  THB: "Thai Baht",
  IDR: "Indonesian Rupiah",
  MYR: "Malaysian Ringgit",
  PHP: "Philippine Peso",
  NZD: "New Zealand Dollar",
  TWD: "Taiwan Dollar",
  BTC: "Bitcoin",
  ETH: "Ethereum",
  USDT: "Tether",
  USDC: "USD Coin",
};

/**
 * Get the currency symbol for a given currency code
 * @param currency - Currency code (e.g., "USD", "EUR") or the resolved currency_symbol from API
 * @returns Currency symbol (e.g., "$", "€")
 */
export function getCurrencySymbol(currency?: string): string {
  if (!currency) return "$";
  // First check if it's a known currency code
  const upperCurrency = currency.toUpperCase();
  if (CURRENCY_SYMBOLS[upperCurrency]) {
    return CURRENCY_SYMBOLS[upperCurrency];
  }
  // If it looks like a UUID (asset_id), return default
  if (currency.includes("-") && currency.length > 30) {
    return "$"; // Fallback for unresolved asset_id
  }
  return currency;
}

/**
 * Get the currency name for a given currency code
 * @param currency - Currency code (e.g., "USD", "EUR")
 * @returns Currency name (e.g., "US Dollar", "Euro")
 */
export function getCurrencyName(currency?: string): string {
  if (!currency) return "US Dollar";
  return CURRENCY_NAMES[currency.toUpperCase()] || currency;
}

/**
 * Format a number as currency with the appropriate symbol
 * @param value - The numeric value
 * @param currency - Currency code
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted currency string (e.g., "$1,234.56")
 */
export function formatCurrency(value?: number, currency?: string, decimals?: number): string {
  if (value === undefined || value === null) return "-";
  const symbol = getCurrencySymbol(currency);
  const d = decimals ?? (value < 1 ? 4 : 2);
  return `${symbol}${value.toLocaleString(undefined, {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  })}`;
}

/**
 * Format a large number as currency with abbreviation (K, M, B, T)
 * @param value - The numeric value
 * @param currency - Currency code
 * @returns Formatted currency string (e.g., "$1.23B")
 */
export function formatLargeCurrency(value?: number, currency?: string): string {
  if (!value) return "-";
  const symbol = getCurrencySymbol(currency);
  if (value >= 1e12) return `${symbol}${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `${symbol}${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `${symbol}${(value / 1e6).toFixed(0)}M`;
  if (value >= 1e3) return `${symbol}${(value / 1e3).toFixed(0)}K`;
  return `${symbol}${value.toLocaleString()}`;
}

/**
 * Format a price with appropriate decimal places
 * @param value - The numeric value
 * @param currency - Currency code
 * @returns Formatted price string
 */
export function formatPrice(value?: number, currency?: string): string {
  if (value === undefined || value === null) return "-";
  const symbol = getCurrencySymbol(currency);
  const decimals = value < 1 ? 6 : value < 100 ? 4 : 2;
  return `${symbol}${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: decimals,
  })}`;
}

